import React, { forwardRef } from "react";
import { WelcomeScreen } from "./WelcomeScreen";
import { IntroScreen } from "./IntroScreen";
import { StateCheckScreen } from "./StateCheckScreen";
import { AccountScreen, AccountData } from "./AccountScreen";
import { AccountRegister, type AccountDetailsStep } from "./AccountRegister";
import { HealthQuestionsIntroScreen } from "./HealthQuestionsIntroScreen";
import { Questionnaire } from "./questionnaire/Questionnaire";
import { QuestionnaireSummaryScreen } from "./QuestionnaireSummaryScreen";
import { DoseSelection } from "./DoseSelection";
import { QuantitySelection } from "./QuantitySelection";
import { SubscriptionSelection } from "./SubscriptionSelection";
import { PatientShippingInfo, ShippingInfoData } from "./PatientShippingInfo";
import { FileUpload } from "./FileUpload";
import { QuestionnaireAnswers, QuestionnaireQuestion } from "../types/questionnaire";
import { getVisibleQuestions } from "../lib/questionnaire-engine";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { AccountSubStep, ProgressSectionId } from "../lib/progress-calculator";
import type { GenderAudience } from "../lib/gender-purchase-eligibility";
// Single source of truth for FlowStep lives in lib/flow-steps.ts; re-exported here
// so existing `import { FlowStep } from "./components/FlowStepRenderer"` call sites don't change.
import type { FlowStep } from "../lib/flow-steps";
export type { FlowStep } from "../lib/flow-steps";
import { getAdjacentStep, EMAIL_CAPTURE_AFTER_QUESTION_COUNT } from "../lib/flow-steps";

// Cart data structure for checkout persistence
export interface CheckoutCartData {
  items: {
    id: string;
    quantity: number;
    selling_plan?: string;
    properties?: Record<string, string>;
  }[];
  checkoutParams: Record<string, string>;
}

export interface FlowState {
  step: FlowStep;
  accountData: AccountData | null;
  patientInfo: any | null;
  answers: QuestionnaireAnswers;
  doseSelection: any | null;
  quantitySelection: any | null;
  subscriptionSelection: any | null;
  shippingInfo: any | null;
  caseId?: string;
  accountSubStep?: AccountSubStep;
  currentQuestionId?: string;
  checkoutUrl?: string;
  checkoutCartData?: CheckoutCartData;
  selectedState?: string;
  questionnaireReturnStep?: FlowStep;
  accountDetailsStep?: AccountDetailsStep;
  accountReturnSubStep?: AccountSubStep;
  accountReturnAccountDetailsStep?: AccountDetailsStep;
  /** SRX-202: where "back" from email-capture should go — 'questionnaire' when reached via the
   *  mid-questionnaire interrupt, unset for the existing Welcome-shortcut paths (back to state-check). */
  emailCaptureReturnStep?: FlowStep;
  /** SRX-202: the question to resume at once email-capture is actually completed (the pending
   *  "next" question at the moment of the interrupt). Kept separate from `currentQuestionId`,
   *  which deliberately stays at the last-answered question so "back" resumes there instead. */
  emailCaptureResumeQuestionId?: string;
}

interface FlowStepRendererProps {
  flowState: FlowState;
  setFlowState: React.Dispatch<React.SetStateAction<FlowState>>;
  isInitializing: boolean;
  isLoading: boolean;
  questions: QuestionnaireQuestion[];
  language: "en" | "es";
  currentStep: number;
  totalSteps: number;
  sectionIndex: number;
  totalSections: number;
  sectionId: ProgressSectionId;
  logoUrl?: string;
  logoUrlAlt?: string;
  welcomeHeroImage?: string;
  welcomeHeroImageAlt?: string;
  productTitle?: string;
  productHandle?: string;
  productTemplate?: string;
  customerTags?: string;
  productGenderAudience?: GenderAudience;
  product: any;
  cookie: any;
  customerId?: string;
  customerEmail?: string;
  customerAccessToken?: string | null;
  productUrl?: string;
  consultationProductPrice?: string;
  consultationProductId?: string;
  consultationProductHandle?: string;
  consultationProductTitle?: string;
  consultationPageTitle?: string;
  shippingFee?: string;
  shippingFeeEnabled?: string;
  fileUploadGuideTitle?: string;
  pendingUploadFile?: File | null;
  onPendingUploadFileChange?: (file: File | null) => void;
  // Event handlers
  onWelcomeContinue: () => void;
  onIntroContinue: () => void;
  onStateCheckContinue: (state: string) => void;
  onCloseModal: () => void;
  onAccountBack: () => void;
  onAccountDetailsComplete: (data: AccountData) => void;
  onHealthQuestionsIntroContinue: () => void;
  onQuestionnaireEmailCapture: (resumeAtQuestionId: string) => void;
  onQuestionnaireComplete: (answers: QuestionnaireAnswers) => void;
  onQuestionnaireSummaryContinue: () => void;
  onDoseSelection: (selectedDose: string) => void;
  onQuantitySelection: (selectedQuantity: string) => void;
  onSubscriptionSelection: (selectedFrequency: string, sellingPlanId: string) => void;
  onShippingInfoComplete: (data: ShippingInfoData) => Promise<void>;
  onFileUploadSuccess: (response: any) => Promise<void>;
  onReset: () => void;
  onNext: (step: FlowStep, data?: Partial<FlowState>) => void | Promise<void>;
  onExitQuestionnaire: () => void;
  updateCookie: (progress: string, data?: object) => Promise<void>;
}

const FlowStepRenderer = forwardRef<HTMLDivElement, FlowStepRendererProps>(({
  flowState,
  setFlowState,
  isInitializing,
  isLoading,
  questions,
  language,
  currentStep,
  totalSteps,
  sectionIndex,
  totalSections,
  sectionId,
  logoUrl,
  logoUrlAlt,
  welcomeHeroImage,
  welcomeHeroImageAlt,
  productTitle,
  productHandle,
  productTemplate,
  customerTags,
  productGenderAudience = "all",
  product,
  cookie,
  customerId,
  customerEmail,
  customerAccessToken,
  productUrl,
  consultationProductPrice,
  consultationProductId,
  consultationProductHandle,
  consultationProductTitle,
  consultationPageTitle,
  shippingFee,
  shippingFeeEnabled,
  fileUploadGuideTitle,
  pendingUploadFile,
  onPendingUploadFileChange,
  onWelcomeContinue,
  onIntroContinue,
  onStateCheckContinue,
  onCloseModal,
  onAccountBack,
  onAccountDetailsComplete,
  onHealthQuestionsIntroContinue,
  onQuestionnaireEmailCapture,
  onQuestionnaireComplete,
  onQuestionnaireSummaryContinue,
  onDoseSelection,
  onQuantitySelection,
  onSubscriptionSelection,
  onShippingInfoComplete,
  onFileUploadSuccess,
  onReset,
  onNext,
  onExitQuestionnaire,
  updateCookie,
}, ref) => {
  // Show loading state while initializing (fetching progress)
  if (isInitializing) {
    return (
      <div
        ref={ref}
        className="fixed inset-0 z-50 overflow-y-auto bg-white"
        role="dialog"
        aria-modal="true"
        aria-label="Medical questionnaire"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <div className="min-h-screen bg-gradient-to-b from-[#FAFAFA] to-[#E8F0F3] flex items-center justify-center p-4">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#2D55FB]"></div>
            <p className="mt-4 text-gray-600">Cargando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading && flowState.step === 'questionnaire') {
    return (
      <div
        ref={ref}
        className="fixed inset-0 z-50 overflow-y-auto bg-white"
        role="dialog"
        aria-modal="true"
        aria-label="Medical questionnaire"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full p-8 shadow-elevated">
            <div className="space-y-6">
              <div className="flex items-center justify-center mb-6">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <Skeleton className="h-4 w-3/4 mx-auto" />
              <Skeleton className="h-4 w-1/2 mx-auto" />
              <div className="space-y-3 pt-6">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (flowState.step) {
      case 'welcome':
        return (
          <WelcomeScreen
            onContinue={onWelcomeContinue}
            onClose={onCloseModal}
            logoUrl={logoUrl}
            logoUrlAlt={logoUrlAlt}
            heroImageUrl={welcomeHeroImage}
            heroImageAlt={welcomeHeroImageAlt}
            language={language}
            productTitle={productTitle}
            productHandle={productHandle}
            customerTags={customerTags}
          />
        );

      case 'intro':
        return (
          <IntroScreen
            onContinue={onIntroContinue}
            onBack={() => onNext('welcome')}
            onClose={onCloseModal}
            language={language}
            logoUrl={logoUrl}
            logoUrlAlt={logoUrlAlt}
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
          />
        );

      case 'state-check':
        return (
          <StateCheckScreen
            onContinue={onStateCheckContinue}
            initialState={flowState.selectedState || ''}
            onBack={() => onNext('intro')}
            language={language}
            logoUrl={logoUrl}
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
          />
        );

      case 'login':
        return (
          <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
            <Card className="max-w-2xl w-full p-8">
              <h2 className="text-2xl font-bold mb-4">Login (Coming Soon)</h2>
              <Button onClick={() => onNext('welcome')}>Back to Welcome</Button>
            </Card>
          </div>
        );

      case 'email-capture':
        return (
          <AccountScreen
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
            onBack={onAccountBack}
            onClose={onCloseModal}
            language={language}
            logoUrl={logoUrl}
            productGenderAudience={productGenderAudience}
            onContinue={(data) =>
              onNext("health-questions-intro", {
                accountData: { ...(flowState.accountData ?? {}), ...data },
              })
            }
            initialAccountData={flowState.accountData}
            onSubStepChange={(subStep) => setFlowState(prev => ({ ...prev, accountSubStep: subStep }))}
            initialSubStep={flowState.accountSubStep}
            initialEmail={flowState.accountData?.email}
            initialMdiCustomerId={flowState.accountData?.customer_id}
          />
        );

      case 'health-questions-intro':
        return (
          <HealthQuestionsIntroScreen
            onContinue={onHealthQuestionsIntroContinue}
            onBack={() => onNext(getAdjacentStep('health-questions-intro', -1, flowState))}
            onClose={onCloseModal}
            language={language}
            logoUrl={logoUrl}
            logoUrlAlt={logoUrlAlt}
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
          />
        );

      case 'account-details':
        return (
          <AccountRegister
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
            logoUrl={logoUrl}
            language={language}
            email={flowState.accountData?.email || customerEmail || ''}
            initialAccountData={flowState.accountData}
            selectedState={flowState.accountData?.state || flowState.selectedState || ''}
            accountDetailsStep={flowState.accountDetailsStep || 'identity'}
            onAccountDetailsStepChange={(step) =>
              setFlowState(prev => ({ ...prev, accountDetailsStep: step }))
            }
            customerId={flowState.accountData?.shopify_customer_id || customerId || null}
            shopifyCustomerId={flowState.accountData?.shopify_customer_id || null}
            customerAccessToken={customerAccessToken || flowState.accountData?.customerAccessToken || null}
            onBack={() => onNext(getAdjacentStep('account-details', -1, flowState))}
            onClose={onCloseModal}
            onContinue={onAccountDetailsComplete}
          />
        );

      case 'questionnaire':
        return (
          <Questionnaire
            questions={questions}
            onComplete={onQuestionnaireComplete}
            language={language}
            answers={flowState.answers}
            setAnswers={(answers) => setFlowState(prev => ({ ...prev, answers }))}
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
            onQuestionChange={(questionId) => setFlowState(prev => ({ ...prev, currentQuestionId: questionId }))}
            logoUrl={logoUrl}
            currentSavedQuestionId={(cookie && typeof cookie === 'object') ? (cookie.currentQuestionId ?? flowState.currentQuestionId ?? "") : (flowState.currentQuestionId ?? "")}
            onClose={onCloseModal}
            onExitQuestionnaire={onExitQuestionnaire}
            updateCookie={updateCookie}
            cookie={(cookie && typeof cookie === 'object') ? cookie : {}}
            savedCaseId={flowState.caseId}
            hideBackOnFirstQuestion={(() => {
              const authSubStep = flowState.accountReturnSubStep ?? flowState.accountSubStep;
              return authSubStep === 'login';
            })()}
            emailCaptureThreshold={EMAIL_CAPTURE_AFTER_QUESTION_COUNT}
            hasCapturedEmail={!!flowState.accountData?.email}
            onEmailCaptureInterrupt={onQuestionnaireEmailCapture}
            // Once the account exists (new this session, or restored), stepping back off the
            // first post-interrupt question should behave like a normal "previous question" —
            // there's no email/password screen worth re-entering on the way back.
            canGoBackToEmailCapture={
              flowState.emailCaptureReturnStep === 'questionnaire' && !flowState.accountData?.has_shopify_account
            }
            onBackToEmailCapture={(prevQuestionId) =>
              onNext('health-questions-intro', { currentQuestionId: prevQuestionId })
            }
          />
        );

      case 'questionnaire-summary':
        return (
          <QuestionnaireSummaryScreen
            onContinue={onQuestionnaireSummaryContinue}
            onBack={() => {
              const visible = getVisibleQuestions(questions, flowState.answers);
              const lastQuestionId =
                visible.length > 0
                  ? visible[visible.length - 1].partner_questionnaire_question_id
                  : "";
              onNext("questionnaire", {
                currentQuestionId: lastQuestionId || flowState.currentQuestionId || "",
              });
            }}
            onClose={onCloseModal}
            language={language}
            logoUrl={logoUrl}
            logoUrlAlt={logoUrlAlt}
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
          />
        );

      case 'dose-selection':
        return (
          <DoseSelection
            onContinue={onDoseSelection}
            onBack={() => onNext('questionnaire-summary')}
            onClose={onCloseModal}
            language={language}
            logoUrl={logoUrl}
            productOptions={product?.options}
            productVariants={product?.variants}
            productTitle={product?.title}
            productTemplate={productTemplate}
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
            chosenDetails={(cookie && typeof cookie === 'object') ? (cookie.doseSelection ?? "") : ""}
          />
        );

      case 'quantity-selection': {
        const frequencyGuideMetafield = product?.metafields?.find(
          (mf: any) => mf?.key === 'frequency_guide_page'
        );
        const frequencyGuidePage = frequencyGuideMetafield?.reference || null;

        return (
          <QuantitySelection
            onContinue={onQuantitySelection}
            onBack={() => onNext('dose-selection')}
            onClose={onCloseModal}
            language={language}
            logoUrl={logoUrl}
            productOptions={product?.options}
            productVariants={product?.variants}
            frequencyGuidePage={frequencyGuidePage}
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
            chosenDetails={(cookie && typeof cookie === 'object') ? (cookie.quantitySelection ?? "") : ""}
          />
        );
      }

      case 'subscription-selection':
        return (
          <SubscriptionSelection
            onContinue={onSubscriptionSelection}
            onEditDose={() => onNext('dose-selection')}
            onEditQuantity={() => onNext('quantity-selection')}
            onBack={() => onNext('quantity-selection')}
            onClose={onCloseModal}
            language={language}
            logoUrl={logoUrl}
            productData={product}
            selectedDose={flowState.doseSelection || ''}
            selectedQuantity={flowState.quantitySelection || ''}
            consultationFee={consultationProductPrice ? parseFloat(consultationProductPrice) : 30}
            consultationProductId={consultationProductId}
            consultationProductHandle={consultationProductHandle}
            consultationProductTitle={consultationProductTitle || ''}
            consultationPageTitle={consultationPageTitle || ''}
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
            chosenFrequency={(cookie && typeof cookie === 'object') ? (cookie.subscriptionSelection?.frequency ?? "") : ""}
            chosenSellingPlanId={(cookie && typeof cookie === 'object') ? (cookie.subscriptionSelection?.sellingPlanId ?? "") : ""}
            updateCookie={updateCookie}
            productHandle={productHandle}
            shippingFee={shippingFee ? parseFloat(shippingFee) : 13}
            shippingFeeEnabled={shippingFeeEnabled === 'true'}
            selectedState={flowState.accountData?.state || flowState.selectedState}
          />
        );

      case 'shipping-info':
        return (
          <PatientShippingInfo
            onContinue={onShippingInfoComplete}
            onBack={() => onNext(getAdjacentStep('shipping-info', -1, flowState))}
            onClose={onCloseModal}
            language={language}
            logoUrl={logoUrl}
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
            initialAddress={flowState.shippingInfo?.address || ''}
            initialAddress2={flowState.shippingInfo?.address2 || ''}
            initialCity={flowState.shippingInfo?.city || ''}
            initialZipCode={flowState.shippingInfo?.zip_code || ''}
            initialState={flowState.shippingInfo?.state || flowState.accountData?.state || flowState.selectedState || ''}
            initialFirstName={flowState.accountData?.firstName || ''}
            initialLastName={flowState.accountData?.lastName || ''}
            customerId={flowState.accountData?.shopify_customer_id || customerId || ''}
            patientId={flowState.accountData?.customer_id || ''}
            email={flowState.accountData?.email || ''}
            phone={flowState.accountData?.phone || ''}
          />
        );

      case 'file-upload':
        return (
          <FileUpload
            patientId={flowState.accountData?.customer_id || ''}
            caseId={flowState.caseId || ''}
            shopifyCustomerId={flowState.accountData?.shopify_customer_id || customerId || ''}
            email={flowState.accountData?.email || customerEmail || ''}
            productUrl={productUrl || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : '')}
            customerAccessToken={customerAccessToken || flowState.accountData?.customerAccessToken || null}
            initialFile={pendingUploadFile}
            onFileChange={onPendingUploadFileChange}
            onSuccess={onFileUploadSuccess}
            onBack={() => onNext('shipping-info')}
            onClose={onCloseModal}
            language={language}
            logoUrl={logoUrl}
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
            fileUploadGuideTitle={fileUploadGuideTitle}
          />
        );

      case 'cart-summary':
        return (
          <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
            <Card className="max-w-2xl w-full p-8">
              <h2 className="text-2xl font-bold mb-4">
                Cart Summary (Coming Soon)
              </h2>
              <p className="mb-4">Selected Dose: {flowState.doseSelection}</p>
              <p className="mb-4">Selected Quantity: {flowState.quantitySelection}</p>
              <Button onClick={onReset}>Start Over</Button>
            </Card>
          </div>
        );

      case 'complete':
        return (
          <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
            <Card className="max-w-2xl w-full p-8 text-center shadow-elevated">
              <div className="space-y-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10">
                  <CheckCircle2 className="w-8 h-8 text-success" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">Thank You!</h1>
                  <p className="text-muted-foreground">
                    Your information has been submitted successfully.
                  </p>
                </div>
                <Button onClick={onReset} variant="outline" className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Start New Form
                </Button>
              </div>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      ref={ref}
      className="fixed inset-0 z-50 overflow-y-auto bg-white"
      role="dialog"
      aria-modal="true"
      aria-label="Medical questionnaire"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {renderContent()}
    </div>
  );
});

FlowStepRenderer.displayName = 'FlowStepRenderer';

export { FlowStepRenderer };
